import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const COLOR_PRESETS = [
  '#e74c3c', '#c0392b', '#e67e22', '#f39c12',
  '#27ae60', '#16a085', '#1abc9c', '#3498db',
  '#2980b9', '#9b59b6', '#8e44ad', '#2c3e50',
  '#34495e', '#95a5a6', '#7f8c8d',
];

const ExpenseTypeFormModal = ({ expenseType, onClose, onSaved }) => {
  const isEdit = Boolean(expenseType);

  const [form, setForm] = useState({
    name: '',
    color: '#7f8c8d',
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (expenseType) {
      setForm({
        name: expenseType.name || '',
        color: expenseType.color || '#7f8c8d',
        is_active: expenseType.is_active !== undefined ? expenseType.is_active : true,
      });
    }
  }, [expenseType]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim()) return alert('❌ Name is required.');

    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim(),
        color: form.color,
        is_active: form.is_active,
      };

      if (isEdit) {
        await chemicalService.updateExpenseType(expenseType.id, payload);
        alert('✅ Expense type updated successfully!');
      } else {
        await chemicalService.createExpenseType(payload);
        alert('✅ Expense type created successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Expense Type' : '➕ New Expense Type'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? `Updating "${expenseType.name}"` : 'Create a new category for expenses'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                style={styles.input}
                placeholder="e.g. Electricity Bill, Rent, Fuel"
                required
                autoFocus
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Color</label>
              <div style={styles.colorPresets}>
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => handleChange('color', c)}
                    style={{
                      ...styles.colorSwatch,
                      background: c,
                      border: form.color === c ? '3px solid #2c3e50' : '3px solid transparent',
                    }}
                    title={c}
                  />
                ))}
              </div>
              <div style={styles.colorPreview}>
                <span style={styles.colorPreviewLabel}>Preview:</span>
                <span style={{
                  ...styles.colorPreviewBadge,
                  background: form.color + '20',
                  color: form.color,
                  border: `1px solid ${form.color}40`,
                }}>
                  {form.name.trim() || 'Sample Type'}
                </span>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>
                  Active (uncheck to retire — won't appear in expense dropdowns)
                </span>
              </label>
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update' : '💾 Create')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '520px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  formGroup: {
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '16px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },

  colorPresets: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: '6px',
    marginTop: '4px',
  },
  colorSwatch: {
    width: '100%',
    aspectRatio: '1',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'transform 0.1s',
  },
  colorPreview: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 0', marginTop: '8px',
  },
  colorPreviewLabel: {
    fontSize: '12px', color: '#95a5a6', fontWeight: '600',
  },
  colorPreviewBadge: {
    padding: '6px 14px', borderRadius: '12px',
    fontSize: '13px', fontWeight: '700',
  },

  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default ExpenseTypeFormModal;